import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip, IconButton, Tooltip } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { TrendingUp, TrendingDown, Star, RateReview, ThumbDown, Reviews, Info } from '@mui/icons-material';

// Modern animations
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const MetricsContainer = styled(Grid)(({ theme }) => ({
  gap: '12px',
  marginBottom: '16px',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    gap: '16px',
    marginBottom: '24px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '24px',
    marginBottom: '32px'
  }
}));

const MetricCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  border: '1px solid rgba(11, 145, 214, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  animation: `${slideUp} 0.6s ease-out`,
  width: '100%',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.98)'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #0B91D6, #34A853, #EF232A)',
    opacity: 0,
    transition: 'opacity 0.3s ease'
  },
  [theme.breakpoints.up('sm')]: {
    borderRadius: '14px'
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
      borderColor: 'rgba(11, 145, 214, 0.2)'
    },
    '&:hover::before': {
      opacity: 1
    },
    '&:active': {
      transform: 'translateY(-4px)'
    }
  }
}));

const MetricHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
  [theme.breakpoints.up('md')]: {
    marginBottom: '16px'
  }
}));

const MetricTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 600,
  color: '#666',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    gap: '8px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px'
  }
}));

const MetricIcon = styled(Box)(({ theme, color }) => ({
  width: '28px',
  height: '28px',
  borderRadius: '6px',
  backgroundColor: `${color}15`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    color: color,
    fontSize: '14px'
  },
  [theme.breakpoints.up('md')]: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    '& svg': {
      fontSize: '18px'
    }
  }
}));

const ComparisonRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 0',
  borderBottom: '1px solid rgba(11, 145, 214, 0.08)',
  '&:last-child': {
    borderBottom: 'none'
  },
  [theme.breakpoints.up('md')]: {
    padding: '12px 0'
  }
}));

const BusinessLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 500,
  color: '#121927',
  minWidth: '70px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    minWidth: '80px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '13px',
    minWidth: '100px'
  }
}));

const MetricValue = styled(Typography)(({ theme, isYours }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: isYours ? '14px' : '12px',
  fontWeight: isYours ? 700 : 600,
  color: isYours ? '#0B91D6' : '#121927',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  animation: isYours ? `${pulse} 2s infinite` : 'none',
  [theme.breakpoints.up('sm')]: {
    fontSize: isYours ? '16px' : '13px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: isYours ? '18px' : '14px'
  }
}));

const PerformanceChip = styled(Chip)(({ theme, performance }) => {
  const getColors = () => {
    switch (performance) {
      case 'excellent':
        return { bg: '#34A85315', color: '#34A853', border: '#34A85330' };
      case 'good':
        return { bg: '#0B91D615', color: '#0B91D6', border: '#0B91D630' };
      case 'average':
        return { bg: '#F59E0B15', color: '#F59E0B', border: '#F59E0B30' };
      case 'poor':
        return { bg: '#EF232A15', color: '#EF232A', border: '#EF232A30' };
      default:
        return { bg: '#F3F4F615', color: '#666', border: '#E5E7EB' };
    }
  };

  const colors = getColors();

  return {
    backgroundColor: colors.bg,
    color: colors.color,
    border: `1px solid ${colors.border}`,
    fontFamily: 'Inter, sans-serif',
    fontSize: '10px',
    fontWeight: 600,
    height: '22px',
    '& .MuiChip-label': {
      padding: '0 6px'
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '11px',
      height: '24px',
      '& .MuiChip-label': {
        padding: '0 8px'
      }
    }
  };
});

const BenchmarkingTableModern = ({ reputationData }) => {
  // Use real data if available, otherwise fallback to mock data
  const data = reputationData || {
    yourBusiness: { rating: 4.6, responseRate: 52, negativeRatio: 6, reviewCount: 20 },
    cityAverage: { rating: 4.2, responseRate: 36, negativeRatio: 23, reviewCount: 180 },
    topCompetitor: { rating: 4.4, responseRate: 48, negativeRatio: 44, reviewCount: 60 }
  };

  // Helpers: safe parsing and consistent formatting
  const safeNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  };

  const clamp = (n, min = 0, max = 100) => Math.min(max, Math.max(min, n));
  const numberFormatter = new Intl.NumberFormat();
  const safeRating = (value) => safeNumber(value, 4.0).toFixed(1);
  const safePercentage = (value) => clamp(Math.round(safeNumber(value, 0)), 0, 100);
  const safeCount = (value) => numberFormatter.format(Math.max(0, Math.round(safeNumber(value, 0))));

  // Performance evaluation functions
  const getRatingPerformance = (rating, comparison) => {
    const diff = rating - comparison;
    if (diff >= 0.3) return 'excellent';
    if (diff >= 0.1) return 'good';
    if (diff >= -0.1) return 'average';
    return 'poor';
  };

  const getResponseRatePerformance = (rate, comparison) => {
    const diff = rate - comparison;
    if (diff >= 20) return 'excellent';
    if (diff >= 10) return 'good';
    if (diff >= -5) return 'average';
    return 'poor';
  };

  const getNegativeRatioPerformance = (ratio, comparison) => {
    const diff = comparison - ratio; // Lower is better for negative ratio
    if (diff >= 10) return 'excellent';
    if (diff >= 5) return 'good';
    if (diff >= -2) return 'average';
    return 'poor';
  };

  const getPerformanceLabel = (performance) => {
    switch (performance) {
      case 'excellent': return 'Excellent';
      case 'good': return 'Good';
      case 'average': return 'Average';
      case 'poor': return 'Needs Work';
      default: return 'Average';
    }
  };

  const metrics = [
    {
      title: 'Average Rating',
      icon: Star,
      color: '#0B91D6',
      yourValue: safeRating(data.yourBusiness.rating),
      cityValue: safeRating(data.cityAverage.rating),
      competitorValue: safeRating(data.topCompetitor.rating),
      performance: getRatingPerformance(
        safeNumber(data.yourBusiness.rating),
        safeNumber(data.cityAverage.rating)
      ),
      suffix: '★',
      tooltip: 'Higher ratings indicate better customer satisfaction'
    },
    {
      title: 'Response Rate',
      icon: RateReview,
      color: '#34A853',
      yourValue: `${safePercentage(data.yourBusiness.responseRate)}%`,
      cityValue: `${safePercentage(data.cityAverage.responseRate)}%`,
      competitorValue: `${safePercentage(data.topCompetitor.responseRate)}%`,
      performance: getResponseRatePerformance(
        safeNumber(data.yourBusiness.responseRate),
        safeNumber(data.cityAverage.responseRate)
      ),
      suffix: '',
      tooltip: 'Percentage of reviews that received a response from the business',
      comingSoon: true // DataForSEO doesn't provide this data for competitors
    },
    {
      title: 'Negative Reviews',
      icon: ThumbDown,
      color: '#EF232A',
      yourValue: `${safePercentage(data.yourBusiness.negativeRatio)}%`,
      cityValue: `${safePercentage(data.cityAverage.negativeRatio)}%`,
      competitorValue: `${safePercentage(data.topCompetitor.negativeRatio)}%`,
      performance: getNegativeRatioPerformance(
        safeNumber(data.yourBusiness.negativeRatio),
        safeNumber(data.cityAverage.negativeRatio)
      ),
      suffix: '',
      tooltip: 'Percentage of reviews with 1-2 star ratings',
      comingSoon: true // DataForSEO doesn't provide this data for competitors
    },
    {
      title: 'Total Reviews',
      icon: Reviews,
      color: '#6366F1',
      yourValue: safeCount(data.yourBusiness.reviewCount),
      cityValue: safeCount(data.cityAverage.reviewCount),
      competitorValue: safeCount(data.topCompetitor.reviewCount),
      performance: 'average',
      suffix: '',
      tooltip: 'Total number of customer reviews received'
    }
  ];

  return (
    <MetricsContainer container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
      {metrics.map((metric, index) => (
        <Grid item xs={12} sm={6} lg={3} key={index}>
          <MetricCard>
            <CardContent sx={{
              padding: { xs: '16px !important', sm: '20px !important', md: '24px !important' }
            }}>
              <MetricHeader>
                <MetricTitle>
                  <MetricIcon color={metric.color}>
                    <metric.icon />
                  </MetricIcon>
                  {metric.title}
                </MetricTitle>
                <Tooltip title={metric.tooltip} arrow>
                  <IconButton size="small" sx={{ color: '#666' }}>
                    <Info fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MetricHeader>
              
              <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
                <PerformanceChip
                  label={getPerformanceLabel(metric.performance)}
                  performance={metric.performance}
                  size="small"
                />
              </Box>

              <Box>
                <ComparisonRow>
                  <BusinessLabel>Your Business</BusinessLabel>
                  <MetricValue isYours={true}>
                    {metric.yourValue}
                    {metric.suffix && (
                      <Box component="span" sx={{ ml: 0.5, opacity: 0.7 }}>
                        {metric.suffix}
                      </Box>
                    )}
                  </MetricValue>
                </ComparisonRow>
                
                <ComparisonRow>
                  <BusinessLabel>City Average</BusinessLabel>
                  {metric.comingSoon ? (
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      borderRadius: '5px',
                      padding: { xs: '3px 8px', md: '4px 10px' },
                      minWidth: { xs: '75px', md: '90px' },
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{
                        fontSize: { xs: '10px', md: '11px' },
                        fontWeight: 600,
                        color: '#6366F1',
                        whiteSpace: 'nowrap'
                      }}>
                        Coming Soon
                      </Typography>
                    </Box>
                  ) : (
                    <MetricValue>
                      {metric.cityValue}
                      {metric.suffix && (
                        <Box component="span" sx={{ ml: 0.5, opacity: 0.7 }}>
                          {metric.suffix}
                        </Box>
                      )}
                    </MetricValue>
                  )}
                </ComparisonRow>
                
                <ComparisonRow>
                  <BusinessLabel>Top Competitor</BusinessLabel>
                  {metric.comingSoon ? (
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      borderRadius: '5px',
                      padding: { xs: '3px 8px', md: '4px 10px' },
                      minWidth: { xs: '75px', md: '90px' },
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{
                        fontSize: { xs: '10px', md: '11px' },
                        fontWeight: 600,
                        color: '#6366F1',
                        whiteSpace: 'nowrap'
                      }}>
                        Coming Soon
                      </Typography>
                    </Box>
                  ) : (
                    <MetricValue>
                      {metric.competitorValue}
                      {metric.suffix && (
                        <Box component="span" sx={{ ml: 0.5, opacity: 0.7 }}>
                          {metric.suffix}
                        </Box>
                      )}
                    </MetricValue>
                  )}
                </ComparisonRow>
              </Box>
            </CardContent>
          </MetricCard>
        </Grid>
      ))}
    </MetricsContainer>
  );
};

export default BenchmarkingTableModern;
